import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { mockHotels } from "../data/mockData";
import { Hotel, Room, BookingDetails } from "../types";
import {
  Check,
  CreditCard,
  Shield,
  Calendar,
  Users,
  Bed,
  MapPin,
  Star,
  ArrowLeft,
} from "lucide-react";

const BookingPage: React.FC = () => {
  const { hotelId, roomId } = useParams<{ hotelId: string; roomId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [hotel, setHotel] = useState<Hotel | null>(null);
  const [room, setRoom] = useState<Room | null>(null);
  const [bookingDetails, setBookingDetails] = useState<BookingDetails>({
    hotelId: hotelId || "",
    roomId: roomId || "",
    checkIn: new Date(searchParams.get("checkIn") || ""),
    checkOut: new Date(searchParams.get("checkOut") || ""),
    guests: parseInt(searchParams.get("guests") || "2"),
    rooms: 1,
    guestInfo: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    },
    totalPrice: 0,
    currency: "USD",
  });
  const [currentStep, setCurrentStep] = useState(1);
  const [isBooking, setIsBooking] = useState(false);

  useEffect(() => {
    const foundHotel = mockHotels.find((h) => h.id === hotelId);
    if (foundHotel) {
      setHotel(foundHotel);
      const foundRoom = foundHotel.rooms.find((r) => r.id === roomId);
      if (foundRoom) {
        setRoom(foundRoom);
        const nights = Math.ceil(
          (bookingDetails.checkOut.getTime() -
            bookingDetails.checkIn.getTime()) /
            (1000 * 60 * 60 * 24)
        );
        const totalPrice = foundRoom.price * nights * bookingDetails.rooms;
        setBookingDetails((prev) => ({
          ...prev,
          totalPrice: totalPrice + Math.round(totalPrice * 0.15), // Add 15% taxes
        }));
      }
    }
  }, [
    hotelId,
    roomId,
    bookingDetails.checkIn,
    bookingDetails.checkOut,
    bookingDetails.rooms,
  ]);

  const handleInputChange = (field: string, value: string) => {
    setBookingDetails((prev) => ({
      ...prev,
      guestInfo: {
        ...prev.guestInfo,
        [field]: value,
      },
    }));
  };

  const handleBooking = async () => {
    setIsBooking(true);
    // Simulate booking process
    await new Promise((resolve) => setTimeout(resolve, 2000));
    setIsBooking(false);
    navigate("/booking-confirmation", {
      state: { bookingDetails, hotel, room },
    });
  };

  const isFormValid = () => {
    return (
      bookingDetails.guestInfo.firstName &&
      bookingDetails.guestInfo.lastName &&
      bookingDetails.guestInfo.email &&
      bookingDetails.guestInfo.phone
    );
  };

  if (!hotel || !room) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  const nights = Math.ceil(
    (bookingDetails.checkOut.getTime() - bookingDetails.checkIn.getTime()) /
      (1000 * 60 * 60 * 24)
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(`/hotel/${hotel.id}`)}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to hotel</span>
            </button>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-600">
                Step {currentStep} of 2
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm p-6">
              {/* Booking Steps */}
              <div className="flex items-center justify-center mb-8">
                <div className="flex items-center space-x-4">
                  <div
                    className={`flex items-center space-x-2 ${
                      currentStep >= 1 ? "text-red-600" : "text-gray-400"
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        currentStep >= 1
                          ? "bg-red-600 text-white"
                          : "bg-gray-200"
                      }`}
                    >
                      {currentStep > 1 ? <Check className="w-4 h-4" /> : "1"}
                    </div>
                    <span className="font-medium">Guest Details</span>
                  </div>
                  <div className="w-8 h-0.5 bg-gray-300"></div>
                  <div
                    className={`flex items-center space-x-2 ${
                      currentStep >= 2 ? "text-red-600" : "text-gray-400"
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        currentStep >= 2
                          ? "bg-red-600 text-white"
                          : "bg-gray-200"
                      }`}
                    >
                      {currentStep > 2 ? <Check className="w-4 h-4" /> : "2"}
                    </div>
                    <span className="font-medium">Confirmation</span>
                  </div>
                </div>
              </div>

              {currentStep === 1 && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Guest Information
                  </h2>
                  <form className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          First Name *
                        </label>
                        <input
                          type="text"
                          value={bookingDetails.guestInfo.firstName}
                          onChange={(e) =>
                            handleInputChange("firstName", e.target.value)
                          }
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Last Name *
                        </label>
                        <input
                          type="text"
                          value={bookingDetails.guestInfo.lastName}
                          onChange={(e) =>
                            handleInputChange("lastName", e.target.value)
                          }
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        value={bookingDetails.guestInfo.email}
                        onChange={(e) =>
                          handleInputChange("email", e.target.value)
                        }
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        value={bookingDetails.guestInfo.phone}
                        onChange={(e) =>
                          handleInputChange("phone", e.target.value)
                        }
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                        required
                      />
                    </div>
                    <div className="pt-4">
                      <button
                        onClick={() => setCurrentStep(2)}
                        disabled={!isFormValid()}
                        className="btn btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Continue to Confirmation
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {currentStep === 2 && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Booking Confirmation
                  </h2>

                  {/* Payment Method */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Payment Method
                    </h3>
                    <div className="border border-gray-300 rounded-lg p-4">
                      <div className="flex items-center space-x-3">
                        <CreditCard className="w-6 h-6 text-gray-400" />
                        <span className="text-gray-700">Pay at Hotel</span>
                        <span className="ml-auto text-sm text-gray-500">
                          No payment required now
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Terms and Conditions */}
                  <div className="mb-6">
                    <label className="flex items-start space-x-3">
                      <input type="checkbox" className="mt-1" required />
                      <span className="text-sm text-gray-700">
                        I agree to the{" "}
                        <button className="text-red-600 hover:underline">
                          Terms and Conditions
                        </button>{" "}
                        and{" "}
                        <button className="text-red-600 hover:underline">
                          Privacy Policy
                        </button>
                      </span>
                    </label>
                  </div>

                  <div className="flex space-x-4">
                    <button
                      onClick={() => setCurrentStep(1)}
                      className="btn btn-secondary flex-1"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleBooking}
                      disabled={isBooking}
                      className="btn btn-primary flex-1 flex items-center justify-center space-x-2"
                    >
                      {isBooking ? (
                        <>
                          <div className="loading-spinner"></div>
                          <span>Processing...</span>
                        </>
                      ) : (
                        <>
                          <Shield className="w-4 h-4" />
                          <span>Complete Booking</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Booking Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-4">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Booking Summary
              </h3>

              {/* Hotel Info */}
              <div className="border-b pb-4 mb-4">
                <h4 className="font-semibold text-gray-900">{hotel.name}</h4>
                <div className="flex items-center text-gray-600 text-sm mt-1">
                  <MapPin className="w-4 h-4 mr-1" />
                  <span>{hotel.location}</span>
                </div>
                <div className="flex items-center space-x-1 mt-2">
                  {Array.from({ length: hotel.stars }, (_, i) => (
                    <Star
                      key={i}
                      className="w-4 h-4 text-yellow-400 fill-current"
                    />
                  ))}
                </div>
              </div>

              {/* Room Info */}
              <div className="border-b pb-4 mb-4">
                <h5 className="font-medium text-gray-900">{room.type}</h5>
                <div className="flex items-center text-gray-600 text-sm mt-1">
                  <Bed className="w-4 h-4 mr-1" />
                  <span>{room.bedType}</span>
                </div>
                <div className="flex items-center text-gray-600 text-sm mt-1">
                  <Users className="w-4 h-4 mr-1" />
                  <span>Up to {room.maxOccupancy} guests</span>
                </div>
              </div>

              {/* Dates */}
              <div className="border-b pb-4 mb-4">
                <div className="flex items-center text-gray-600 text-sm mb-2">
                  <Calendar className="w-4 h-4 mr-2" />
                  <span>
                    {nights} night{nights !== 1 ? "s" : ""}
                  </span>
                </div>
                <div className="text-sm text-gray-700">
                  <div>
                    Check-in: {bookingDetails.checkIn.toLocaleDateString()}
                  </div>
                  <div>
                    Check-out: {bookingDetails.checkOut.toLocaleDateString()}
                  </div>
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>
                    Room rate ({nights} night{nights !== 1 ? "s" : ""})
                  </span>
                  <span>${room.price * nights}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Taxes & fees</span>
                  <span>${Math.round(room.price * nights * 0.15)}</span>
                </div>
                <div className="border-t pt-2">
                  <div className="flex justify-between text-lg font-semibold text-gray-900">
                    <span>Total</span>
                    <span>${bookingDetails.totalPrice}</span>
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-green-50 rounded-lg">
                <div className="flex items-center space-x-2 text-green-700">
                  <Shield className="w-5 h-5" />
                  <span className="text-sm font-medium">Secure Booking</span>
                </div>
                <p className="text-xs text-green-600 mt-1">
                  Your booking is protected by our secure payment system
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
